// Modal "Minha Conta" do Super Admin — 2 abas: Perfil / Segurança (troca senha com 2FA + política forte).
// Após trocar a senha, o backend bumpa token_version → o próximo request vai dar 401 → auto-logout.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, User, ShieldCheck, Mail, KeyRound, Loader2, Check, AlertTriangle, Eye, EyeOff, History } from "lucide-react";

function strength(pwd) {
  if (!pwd) return { score: 0, label: "—", color: "slate", issues: [] };
  const issues = [];
  if (pwd.length < 12) issues.push("Mínimo 12 caracteres");
  if (!/[a-z]/.test(pwd)) issues.push("1 letra minúscula");
  if (!/[A-Z]/.test(pwd)) issues.push("1 letra MAIÚSCULA");
  if (!/\d/.test(pwd)) issues.push("1 número");
  if (/^[a-zA-Z0-9]+$/.test(pwd)) issues.push("1 caractere especial (!@#$%&*)");
  const score = 5 - issues.length;
  const map = ["Muito fraca", "Fraca", "Média", "Boa", "Forte", "Muito forte"];
  const colors = ["red", "red", "amber", "amber", "emerald", "emerald"];
  return { score, label: map[score], color: colors[score], issues };
}

export default function MinhaConta({ onClose }) {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("perfil");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // Perfil
  const [nome, setNome] = useState("");
  const [emailContato, setEmailContato] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Segurança
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [changing, setChanging] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const st = strength(newPwd);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/superadmin/me");
        setMe(r.data);
        setNome(r.data.nome || "");
        setEmailContato(r.data.email_contato || "");
      } catch (e) {
        toast.error("Erro ao carregar perfil");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const salvarPerfil = async () => {
    setSavingProfile(true);
    try {
      await api.patch("/superadmin/me", { nome, email_contato: emailContato });
      toast.success("Perfil atualizado");
      const r = await api.get("/superadmin/me"); setMe(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar perfil");
    } finally { setSavingProfile(false); }
  };

  const pedirCodigo = async () => {
    setSendingCode(true);
    try {
      const r = await api.post("/superadmin/me/request-change-code");
      toast.success(`Código enviado para ${r.data.sent_to}`);
      setCodeSent(true);
      setResendCooldown(60);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar código");
    } finally { setSendingCode(false); }
  };

  const trocarSenha = async () => {
    if (st.issues.length > 0) {
      toast.error("A nova senha ainda não atende a política");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Confirmação de senha não bate");
      return;
    }
    if (!code || code.length !== 6) {
      toast.error("Código 2FA inválido");
      return;
    }
    setChanging(true);
    try {
      await api.post("/superadmin/me/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
        code,
      });
      toast.success("Senha alterada com sucesso. Fazendo logout...");
      setTimeout(() => logout(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao trocar senha");
    } finally { setChanging(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="dialog-minha-conta">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <User size={20} className="text-slate-700" />
            <h2 className="text-lg font-bold text-slate-900">Minha Conta</h2>
          </div>
          <button onClick={onClose} data-testid="btn-close-minha-conta"
            className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-5">
          <button onClick={() => setTab("perfil")} data-testid="tab-perfil"
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "perfil" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <User size={14} className="inline mr-1.5" /> Perfil
          </button>
          <button onClick={() => setTab("seguranca")} data-testid="tab-seguranca"
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "seguranca" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <ShieldCheck size={14} className="inline mr-1.5" /> Segurança
          </button>
        </div>

        {loading && <div className="text-center py-10"><Loader2 size={22} className="animate-spin inline text-slate-400" /></div>}

        {!loading && tab === "perfil" && (
          <div className="p-5 space-y-4" data-testid="panel-perfil">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">E-mail de login</div>
              <div className="text-sm font-mono font-semibold text-slate-900">{me?.email}</div>
              <div className="text-[11px] text-slate-500 mt-1">O e-mail de login não pode ser alterado por aqui — contate suporte Emergent se precisar trocar.</div>
            </div>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Nome de exibição</span>
              <input type="text" value={nome} onChange={(e) => setNome(e.target.value)}
                data-testid="input-nome"
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">E-mail de contato (opcional)</span>
              <input type="email" value={emailContato} onChange={(e) => setEmailContato(e.target.value)}
                placeholder="suporte@suaempresa.com.br"
                data-testid="input-email-contato"
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
              <span className="text-[11px] text-slate-500 mt-1 block">Usado quando um cliente clica em "Falar com Suporte" ou aparece em rodapés de e-mails do sistema.</span>
            </label>
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs text-slate-500">
                Última troca de senha: <span className="font-mono font-semibold">{me?.password_updated_at || "nunca"}</span><br/>
                Versão de token: <span className="font-mono font-semibold">{me?.token_version || 0}</span>
              </div>
            </div>
            <button onClick={salvarPerfil} disabled={savingProfile} data-testid="btn-salvar-perfil"
              className="w-full h-11 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {savingProfile ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Salvar alterações
            </button>
          </div>
        )}

        {!loading && tab === "seguranca" && (
          <div className="p-5 space-y-5" data-testid="panel-seguranca">
            {/* Senha atual */}
            <label className="block">
              <span className="text-xs font-semibold text-slate-700 flex items-center gap-1"><KeyRound size={12} /> Senha atual</span>
              <input type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)}
                data-testid="input-senha-atual" autoComplete="current-password"
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
            </label>

            {/* Nova senha */}
            <div>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Nova senha</span>
                <div className="mt-1 relative">
                  <input type={showNew ? "text" : "password"} value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                    data-testid="input-nova-senha" autoComplete="new-password"
                    className="w-full h-10 px-3 pr-10 rounded-md border border-slate-300 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                  <button type="button" onClick={() => setShowNew(!showNew)}
                    className="absolute right-2 top-2 text-slate-400 hover:text-slate-700">
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>
              {/* Força da senha */}
              {newPwd && (
                <div className="mt-2 space-y-1" data-testid="pwd-strength">
                  <div className="h-1.5 w-full bg-slate-200 rounded">
                    <div className={`h-full rounded transition-all ${st.color === "red" ? "bg-red-500" : st.color === "amber" ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${(st.score / 5) * 100}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className={`font-semibold ${st.color === "red" ? "text-red-600" : st.color === "amber" ? "text-amber-700" : "text-emerald-700"}`}>
                      {st.label}
                    </span>
                    <span className="text-slate-500 font-mono">{newPwd.length} chars</span>
                  </div>
                  {st.issues.length > 0 && (
                    <ul className="text-[11px] text-slate-500 mt-1 space-y-0.5">
                      {st.issues.map((i) => <li key={i} className="flex items-center gap-1"><X size={10} className="text-red-500" /> {i}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Confirmação */}
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Confirme a nova senha</span>
              <input type={showNew ? "text" : "password"} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
                data-testid="input-confirma-senha"
                className={`mt-1 w-full h-10 px-3 rounded-md border text-sm font-mono focus:ring-1 focus:ring-blue-500 ${confirmPwd && newPwd !== confirmPwd ? "border-red-400 focus:border-red-500" : "border-slate-300 focus:border-blue-500"}`} />
              {confirmPwd && newPwd !== confirmPwd && <span className="text-[11px] text-red-600">Não bate com a nova senha</span>}
            </label>

            {/* 2FA */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-blue-700" />
                <span className="text-sm font-bold text-blue-900">Confirmação por e-mail</span>
              </div>
              <p className="text-xs text-blue-800">
                Antes de trocar, você precisa de um código de 6 dígitos enviado pro seu e-mail de login.
              </p>
              <button onClick={pedirCodigo} disabled={sendingCode || resendCooldown > 0}
                data-testid="btn-pedir-codigo"
                className="h-9 px-4 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 flex items-center gap-2">
                {sendingCode ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {resendCooldown > 0 ? `Reenviar em ${resendCooldown}s` : codeSent ? "Reenviar código" : "Enviar código"}
              </button>
              {codeSent && (
                <label className="block mt-2">
                  <span className="text-xs font-semibold text-blue-900">Código de 6 dígitos</span>
                  <input type="text" value={code} maxLength={6} inputMode="numeric"
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="input-codigo-2fa"
                    placeholder="000000"
                    className="mt-1 w-full h-11 px-3 rounded-md border border-blue-300 text-center text-lg font-mono tracking-widest focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                </label>
              )}
            </div>

            {/* Alerta */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded flex items-start gap-2 text-xs text-amber-900">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>Trocar a senha vai <strong>deslogar você de todos os devices</strong> (incluindo este). Você precisará fazer login de novo em todos.</div>
            </div>

            <button onClick={trocarSenha} disabled={changing || !codeSent || st.issues.length > 0 || newPwd !== confirmPwd || !currentPwd || !code}
              data-testid="btn-trocar-senha"
              className="w-full h-11 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {changing ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              Trocar senha agora
            </button>

            {/* Histórico */}
            {me?.ultimas_trocas?.length > 0 && (
              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <History size={14} className="text-slate-500" />
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Últimas trocas</span>
                </div>
                <ul className="text-[11px] text-slate-600 space-y-1" data-testid="historico-trocas">
                  {me.ultimas_trocas.map((h, i) => (
                    <li key={i} className="font-mono bg-slate-50 rounded px-2 py-1">
                      {h.changed_at?.slice(0, 19).replace("T", " ")} • IP {h.changed_from_ip || "—"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
